/**
 * JourneysView -- /twin/journeys (P16.4 / #485).
 *
 * Two-column layout (rail + detail pane) ported from v1's
 * `JourneyView`:
 *
 *   * Rail (left): journeys grouped by IBS name -> per-channel
 *     buttons with the step_count + a click-to-select handler.
 *     Client-side text filter on IBS name / channel / description.
 *   * Detail (right): the selected journey's description + an
 *     ordered step list (numbered circles + name + description).
 *
 * No cascade tinting at v0.16.0 -- backlog #533 picks it up.
 * The cascade overlay surface (CascadeImpactCard's "Customer
 * journeys" section) is the v0.16.0 cross-link path: an operator
 * staring at a degraded IBS sees the journey breadcrumb there,
 * not here.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { Twin3DStage, Twin3DToggle, useTwin3DMode } from "twin/twin3d/Twin3DMode";
import { useJourney, useJourneys } from "twin/api/hooks";

export function JourneysView() {
  const { t } = useTranslation();
  const journeys = useJourneys();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const selected = useJourney(selectedId ?? undefined);

  const grouped = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = journeys.data ?? [];
    const matches = needle
      ? rows.filter((r) =>
          [r.ibs_service_name, r.ibs_service_code, r.channel_name, r.channel_code]
            .join(" ")
            .toLowerCase()
            .includes(needle),
        )
      : rows;
    const byIbs: Record<
      string,
      { ibs_id: string; ibs_name: string; ibs_code: string; rows: typeof matches }
    > = {};
    for (const r of matches) {
      const k = r.ibs_service_id;
      const bucket = byIbs[k] ?? {
        ibs_id: k,
        ibs_name: r.ibs_service_name,
        ibs_code: r.ibs_service_code,
        rows: [],
      };
      bucket.rows.push(r);
      byIbs[k] = bucket;
    }
    return Object.values(byIbs).sort((a, b) => a.ibs_name.localeCompare(b.ibs_name));
  }, [journeys.data, search]);

  const { effectiveMode, setMode, onWebglFail, loadFailed } = useTwin3DMode("journey");

  // Brief two, section 1: flipping to 3D here lands on the JOURNEY
  // view, not the constellation.
  const heading = (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold">
          <MarsadIcon
            name="journey"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("modules.twin.journeys.heading")}
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">
          {t("modules.twin.journeys.subheading")}
        </p>
      </div>
      <Twin3DToggle
        mode={effectiveMode}
        onChange={setMode}
        testid="journeys-view"
        loadFailed={loadFailed}
      />
    </div>
  );

  if (effectiveMode === "3d") {
    return (
      <article className="flex flex-col gap-3" data-testid="journeys-view">
        <header>{heading}</header>
        <Twin3DStage view="journey" onWebglFail={onWebglFail} />
      </article>
    );
  }

  return (
    <article className="max-w-6xl" data-testid="journeys-view">
      <header className="mb-4">{heading}</header>

      <div className="grid grid-cols-12 gap-4">
        {/* ── Rail ───────────────────────────────────────────── */}
        <section
          data-testid="journeys-rail"
          className="col-span-4 border border-slate-200 rounded-lg bg-white p-3 h-fit"
        >
          <input
            dir="auto"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("modules.twin.journeys.searchPlaceholder")}
            data-testid="journeys-search"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm mb-3"
          />
          {journeys.isLoading && (
            <p data-testid="journeys-rail-loading" className="text-sm text-slate-500">
              {t("modules.twin.journeys.loading")}
            </p>
          )}
          {journeys.isError && (
            <p
              data-testid="journeys-rail-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
            >
              {journeys.error.detail}
            </p>
          )}
          {journeys.data && grouped.length === 0 && (
            <p data-testid="journeys-rail-empty" className="text-sm text-slate-500">
              {/* Round 14 B5 -- "No journeys match" on an unfiltered, empty
                  register answered a search nobody had typed. */}
              {journeys.data.length === 0
                ? t("modules.twin.twin3d.empty.journey")
                : t("modules.twin.journeys.empty")}
            </p>
          )}
          <ul className="space-y-3">
            {grouped.map((g) => (
              <li
                key={g.ibs_id}
                data-testid="journeys-rail-group"
                data-ibs-id={g.ibs_id}
              >
                <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                  {g.ibs_name}
                </h3>
                <ul className="space-y-1">
                  {g.rows.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        data-testid="journeys-rail-button"
                        data-journey-id={r.id}
                        data-active={selectedId === r.id}
                        onClick={() => setSelectedId(r.id)}
                        className={`w-full text-start rounded border px-2 py-1.5 text-sm ${
                          selectedId === r.id
                            ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <span className="font-medium">{r.channel_name}</span>
                        {" · "}
                        <span className="ms-2 text-xs text-slate-500 tabular-nums">
                          {t("modules.twin.journeys.steps", { count: r.step_count })}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Detail pane ────────────────────────────────────── */}
        <section
          data-testid="journeys-detail"
          className="col-span-8 border border-slate-200 rounded-lg bg-white p-4"
        >
          {!selectedId && (
            <p data-testid="journeys-detail-prompt" className="text-sm text-slate-500">
              {t("modules.twin.journeys.detail.prompt")}
            </p>
          )}
          {selectedId && selected.isLoading && (
            <p data-testid="journeys-detail-loading" className="text-sm text-slate-500">
              {t("modules.twin.journeys.detail.loading")}
            </p>
          )}
          {selectedId && selected.isError && (
            <p
              data-testid="journeys-detail-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
            >
              {selected.error.detail}
            </p>
          )}
          {selected.data && (
            <>
              <header className="mb-3">
                <h2 className="text-lg font-semibold text-slate-900">
                  {selected.data.ibs_service_name}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  <span data-testid="journeys-detail-channel">
                    {selected.data.channel_name} ({selected.data.channel_code})
                  </span>
                  {" · "}
                  <span className="ms-3 tabular-nums">
                    {t("modules.twin.journeys.steps", {
                      count: selected.data.step_count,
                    })}
                  </span>
                </p>
              </header>
              {selected.data.description && (
                <p
                  data-testid="journeys-detail-narrative"
                  className="italic text-sm text-slate-700 bg-slate-50 rounded p-3 mb-3"
                >
                  {selected.data.description}
                </p>
              )}
              {selected.data.steps.length === 0 ? (
                <p
                  data-testid="journeys-detail-no-steps"
                  className="text-sm text-slate-500"
                >
                  {t("modules.twin.journeys.detail.noSteps")}
                </p>
              ) : (
                <ol data-testid="journeys-detail-steps" className="space-y-2">
                  {selected.data.steps.map((s) => (
                    <li
                      key={s.id}
                      data-testid="journeys-detail-step"
                      data-step-number={s.step_number}
                      className="flex items-baseline gap-3 border-s-2 border-emerald-200 ps-3"
                    >
                      <span className="inline-flex items-center justify-center rounded-full bg-emerald-600 text-white w-6 h-6 text-xs font-medium tabular-nums shrink-0">
                        {s.step_number}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">{s.name}</p>
                        {s.description && (
                          <p className="text-xs text-slate-600 mt-0.5">
                            {s.description}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </section>
      </div>
    </article>
  );
}
